import { fileManagerOpers } from '@/store/modules/fileManager/fileManagerTypes'
import { ActionDuplicateFile, FileInfo, FileStatus } from '../UploaderTypes'
import { ref } from 'vue'
import useStageSelector from '@/components/StageSelector/composable/useStageSelector'
import { templateEntityId as rawTemplateEntityId } from '@/general/entityIds'
import { createFolder, uploadOrReplaceFile } from '@/services/FileManagerService'

interface UploadFolderProps {
  folder: FileInfo
  mode: ActionDuplicateFile
  basePath: string
  entity: { id: string; typeId: string; additionalTypeId?: string }
}

interface UploadFileProps {
  file: FileInfo
  mode: ActionDuplicateFile
  basePath: string
  entity: { id: string; typeId: string; additionalTypeId?: string }
  originalFile?: any
}

interface ResponseUseUploader {
  uploadFolder: (args: UploadFolderProps) => Promise<ResponseUploadFolder>
  uploadFile: (args: UploadFileProps) => Promise<FileStatus>
}

interface ResponseUploadFolder {
  status: FileStatus
  folderId: string
}

export default function useUploader(): ResponseUseUploader {
  const status = ref<FileStatus>(FileStatus.LOADING)
  const folderId = ref<string>('')
  const templateEntityId = String(rawTemplateEntityId)

  const { currentStageId } = useStageSelector()

  const uploadFolder = async ({ folder, mode, basePath }: UploadFolderProps): Promise<ResponseUploadFolder> => {
    try {
      const { name } = folder
      const post = {
        action: fileManagerOpers.CREATE,
        basePath,
        name,
        mode
      }
      const data = await createFolder(post)

      if (data.error) {
        throw new Error(data.error)
      }
      folderId.value = data.idFolder
      status.value = FileStatus.LOADED
    } catch (error) {
      status.value = FileStatus.ERROR
    } finally {
      return {
        status: status.value,
        folderId: folderId.value
      }
    }
  }

  const uploadFile = async (args: UploadFileProps): Promise<FileStatus> => {
    try {
      const { basePath, file, entity, mode, originalFile } = args
      const formData = new FormData()
      formData.append('uploadFiles', file.file as File)
      formData.append('filename', file.name)
      formData.append('basePath', basePath)

      if (mode === 'replace' && isTemplateFromFile(originalFile)) {
        formData.append('idEntity', originalFile.idEntity)
      } else {
        formData.append('idEntity', entity.id)
      }

      formData.append('idEntityType', entity.typeId)

      if (entity.additionalTypeId) {
        formData.append('idEntityTypeAdditional', entity.additionalTypeId)
      }

      if (Boolean(currentStageId.value)) {
        formData.append('idStage', currentStageId.value)
      }

      if (mode === 'replace') {
        formData.append('idDocument', originalFile?.id)
      }

      const data = await uploadOrReplaceFile(formData, mode)

      if (data.error) {
        throw new Error(data.error)
      }
      status.value = FileStatus.LOADED
    } catch (error) {
      status.value = FileStatus.ERROR
    } finally {
      return status.value
    }
  }

  const isTemplateFromFile = (originalFile: any) => {
    return originalFile?.idEntityType === templateEntityId
  }

  return {
    uploadFolder,
    uploadFile
  }
}
